// One list: "Reading…" while the AI extracts (polls every 3 s), or straight to the table when the
// office typed it. Then the review table with editable cells, add-a-row and remove-a-row, issue
// chips, the template + when picker, Schedule, and the result.

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clockWords, dateWords, formatPhone, normalizeE164, zonedTimeToUtc } from '@bt/core';
import { api, assetUrl, money, type ImportRow, type ImportScheduleBody, type ImportScheduleResponse, type ListImport, type MeResponse } from '../../api';
import { Loading, ErrorBox, InlineError } from '../../components/States';
import { useToast } from '../../components/Toast';
import { CATEGORY_LABEL, categoryOf } from '../../components/TemplatePicker';
import { fmtDateAtTime, fmtFull, pluralize } from '../../lib/format';
import { IMPORT_STATUS_WORDS, importTone } from './ListsPage';

type When = 'one_time' | 'per_row';
type Offset = 'day_before_6pm' | 'two_hours_before' | 'custom';

/** An issue that means the row will be skipped (the server decides for real; this is the preview). */
const BLOCKING = /opt|invalid|not a (valid|full)|bad number|no phone|missing phone|no number|duplicate|blocked|do not|dnc|unreadable/i;

export function isBlocking(issue: string): boolean {
  return BLOCKING.test(issue);
}

export function ImportDetailPage({ me }: { me: MeResponse }) {
  const { importId = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({
    queryKey: ['import', importId],
    queryFn: () => api.imports.get(importId),
    enabled: !!importId,
    refetchInterval: (query) => (query.state.data?.import.status === 'extracting' ? 3000 : false),
  });
  const imp = q.data?.import ?? null;

  // ----- editable rows -----
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (imp && !dirty) setRows(imp.rows ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imp?.id, imp?.updated_at, imp?.status]);

  const setCell = (i: number, key: keyof ImportRow, value: string) => {
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [key]: value === '' ? null : value } : r)));
    setDirty(true);
  };
  const dropRow = (i: number) => {
    setRows((rs) => rs.filter((_, j) => j !== i));
    setDirty(true);
  };
  const addRow = () => {
    setRows((rs) => [...rs, { name: null, phone_raw: null, phone_e164: null, address: null, notes: null, appointment_date: null, appointment_time: null, issues: [] }]);
    setDirty(true);
  };

  const save = useMutation({
    mutationFn: () => api.imports.update(importId, rows),
    onSuccess: (r) => {
      setDirty(false);
      setRows(r.import.rows ?? []);
      qc.setQueryData(['import', importId], r);
      toast({ text: 'Rows saved.', tone: 'ok' });
    },
  });

  // ----- the schedule form -----
  const templates = useQuery({ queryKey: ['templates'], queryFn: api.templates.list, staleTime: 30_000 });
  const activeLines = me.lines.filter((l) => l.active !== false);
  const [templateId, setTemplateId] = useState('');
  const [lineId, setLineId] = useState(activeLines[0]?.id ?? '');
  const [when, setWhen] = useState<When>('one_time');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('10:00');
  const [offset, setOffset] = useState<Offset>('day_before_6pm');
  const [customHours, setCustomHours] = useState('24');
  const [result, setResult] = useState<ImportScheduleResponse | null>(null);

  const template = templates.data?.items.find((t) => t.id === templateId) ?? null;
  const orderedTemplates = useMemo(() => {
    const items = templates.data?.items ?? [];
    const rank = { first_message: 0, other: 1, reminder: 2 } as const;
    return [...items].sort((a, b) => rank[categoryOf(a)] - rank[categoryOf(b)] || a.name.localeCompare(b.name));
  }, [templates.data]);
  const fewWordings = !!template && template.variants.filter((v) => v.trim()).length < 3;
  // a wording that says {{time}} with no fallback needs every row to carry one (the server checks
  // this for real; here it just stops the count saying "ready" for a row that will be left out)
  const needs = useMemo(() => {
    const wanted = new Set<string>();
    for (const v of template?.variants ?? []) for (const m of v.matchAll(/\{\{\s*([^{}|]*?)\s*\}\}/g)) wanted.add((m[1] ?? '').trim().toLowerCase());
    return { time: wanted.has('time'), date: wanted.has('date') };
  }, [template]);

  const tz = me.workspace.timezone;
  const sendAt = useMemo(() => {
    if (when !== 'one_time') return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
    const t = /^(\d{2}):(\d{2})$/.exec(time);
    if (!m || !t) return null;
    return zonedTimeToUtc({ year: +m[1], month: +m[2], day: +m[3], hour: +t[1], minute: +t[2] }, tz);
  }, [when, date, time, tz]);
  const inPast = !!sendAt && sendAt.getTime() < Date.now();

  // ----- the summary line -----
  const summary = useMemo(() => {
    let ready = 0;
    const reasons = new Map<string, number>();
    for (const r of rows) {
      const blocking = (r.issues ?? []).filter(isBlocking);
      const noAppt = when === 'per_row' && (!r.appointment_date || !r.appointment_time);
      const noTime = needs.time && !r.appointment_time;
      const noDate = needs.date && !r.appointment_date;
      if (blocking.length === 0 && !noAppt && !noTime && !noDate && (r.phone_e164 || normalizeE164(r.phone_raw ?? ''))) ready++;
      else {
        const why = blocking[0] ?? (noAppt ? 'no appointment date or time' : noTime ? 'no time, and this wording needs one' : noDate ? 'no date, and this wording needs one' : 'no phone number');
        reasons.set(why, (reasons.get(why) ?? 0) + 1);
      }
    }
    return { ready, skipped: rows.length - ready, reasons: [...reasons.entries()] };
  }, [rows, when, needs]);

  const schedule = useMutation({
    mutationFn: async () => {
      if (dirty) {
        const r = await api.imports.update(importId, rows);
        setDirty(false);
        setRows(r.import.rows ?? []);
      }
      const body: ImportScheduleBody = { template_id: templateId, line_id: lineId || undefined };
      if (when === 'one_time') body.send_at = sendAt!.toISOString();
      else {
        body.per_row_appointment = true;
        body.offset = offset === 'custom' ? { preset: 'custom', minutes_before: Math.max(0, Math.round(Number(customHours) * 60)) } : { preset: offset };
      }
      return api.imports.schedule(importId, body);
    },
    onSuccess: (r) => {
      setResult(r);
      qc.invalidateQueries({ queryKey: ['import', importId] });
      qc.invalidateQueries({ queryKey: ['imports'] });
      qc.invalidateQueries({ queryKey: ['scheduled'] });
    },
  });

  const cancel = useMutation({
    mutationFn: () => api.imports.cancel(importId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['imports'] });
      toast({ text: 'List cancelled.', tone: 'ok' });
      navigate('/lists');
    },
    onError: (e) => toast({ text: (e as Error).message, tone: 'error' }),
  });

  if (q.isPending) return <Loading label="Opening the list" />;
  if (q.isError || !imp)
    return (
      <div className="page">
        <ErrorBox error={q.error ?? new Error('List not found')} retry={() => q.refetch()} />
      </div>
    );

  const typed = imp.source === 'manual';
  const canSchedule =
    imp.status === 'review' && !!templateId && !fewWordings && !!lineId && summary.ready > 0 && (when === 'per_row' || (!!sendAt && !inPast)) && !schedule.isPending;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="small">
            <Link to="/lists">Lists</Link> /
          </div>
          <h1>
            {typed ? 'Typed list' : 'List'} from {fmtFull(imp.created_at, tz)} <span className={`chip ${importTone(imp.status)}`}>{IMPORT_STATUS_WORDS[imp.status] ?? imp.status}</span>
          </h1>
          {imp.note && <div className="muted small">{imp.note}</div>}
        </div>
        <div className="row">
          {!typed && <span className="muted small">AI cost for this list: ${money(imp.cost_usd).toFixed(2)}</span>}
          {(imp.status === 'review' || imp.status === 'extracting') && (
            <button className="btn" onClick={() => confirm('Cancel this list? Nothing has been scheduled from it.') && cancel.mutate()} disabled={cancel.isPending}>
              Cancel list
            </button>
          )}
        </div>
      </div>

      {imp.assets && imp.assets.length > 0 && (
        <div className="row wrap" style={{ marginBottom: 12 }}>
          {imp.assets.map((a) => {
            const url = assetUrl(a);
            return url ? (
              <a key={a.id} href={url} target="_blank" rel="noreferrer" className="chip" title={a.filename}>
                {a.kind === 'pdf' ? 'PDF' : 'picture'}: {a.filename}
              </a>
            ) : (
              <span key={a.id} className="chip">
                {a.filename}
              </span>
            );
          })}
        </div>
      )}

      {imp.status === 'extracting' && (
        <div className="card">
          <div className="center">
            <span className="spinner" aria-hidden="true" />
            <span style={{ marginLeft: 8 }}>Reading… this takes about a minute.</span>
          </div>
          <p className="muted small" style={{ textAlign: 'center' }}>You can leave this page; the list keeps reading and shows up under Lists when it is ready.</p>
        </div>
      )}

      {imp.status === 'failed' && (
        <div className="notice danger" style={{ marginBottom: 12 }}>
          Could not read this list{imp.error ? `: ${imp.error}` : '.'} Try a clearer picture, or a screenshot instead of a photo.
        </div>
      )}

      {imp.status === 'cancelled' && <div className="notice" style={{ marginBottom: 12 }}>This list was cancelled. Nothing was scheduled from it.</div>}

      {(imp.status === 'scheduled' || result) && <ResultPanel imp={imp} result={result} tz={tz} />}

      {(imp.status === 'review' || (imp.status === 'scheduled' && rows.length > 0)) && (
        <div className="card" style={{ padding: 0 }}>
          <div className="row between" style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
            <div>
              <strong>{pluralize(rows.length, 'row')}</strong>
              {imp.status === 'review' && (
                <span className="muted small">
                  {' '}
                  · {summary.ready} ready · {summary.skipped} will be skipped
                  {summary.reasons.length > 0 && ` (${summary.reasons.map(([why, n]) => `${n} ${why}`).join(', ')})`}
                </span>
              )}
            </div>
            {imp.status === 'review' && (
              <div className="row">
                {dirty && <span className="tiny warn-text">unsaved edits</span>}
                <button className="btn small" onClick={addRow}>
                  Add a row
                </button>
                <button className="btn small" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
                  {save.isPending ? 'Saving' : 'Save changes'}
                </button>
              </div>
            )}
          </div>
          <InlineError error={save.error} />
          <div className="table-wrap stack">
            <table className={`table ${imp.status === 'review' ? 'edit' : ''}`}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Address</th>
                  <th>Notes</th>
                  <th>Visit day</th>
                  <th>Time</th>
                  <th>Issues</th>
                  {imp.status === 'review' && <th />}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const blocking = (r.issues ?? []).some(isBlocking);
                  const e164 = r.phone_e164 ?? normalizeE164(r.phone_raw ?? '');
                  const ro = imp.status !== 'review';
                  return (
                    <tr key={i} className={blocking ? 'skip' : ''}>
                      <td className="muted row-number" data-label="Row">{i + 1}</td>
                      <td data-label="Name">
                        <Cell value={r.name} onChange={(v) => setCell(i, 'name', v)} readOnly={ro} placeholder="name" />
                      </td>
                      <td data-label="Phone">
                        <Cell value={r.phone_raw ?? r.phone_e164} onChange={(v) => setCell(i, 'phone_raw', v)} readOnly={ro} placeholder="phone" />
                        <div className={`tiny ${e164 ? 'muted' : 'danger-text'}`}>{e164 ? formatPhone(e164) : r.phone_raw ? 'not a full number' : ''}</div>
                      </td>
                      <td data-label="Address">
                        <Cell value={r.address} onChange={(v) => setCell(i, 'address', v)} readOnly={ro} placeholder="address" />
                      </td>
                      <td data-label="Notes">
                        <Cell value={r.notes} onChange={(v) => setCell(i, 'notes', v)} readOnly={ro} placeholder="" />
                      </td>
                      <td data-label="Visit day">
                        <Cell value={r.appointment_date} onChange={(v) => setCell(i, 'appointment_date', v)} readOnly={ro} placeholder="2026-09-04" type="date" display={dateWords(r.appointment_date)} />
                        {!ro && r.appointment_date && <div className="tiny muted">{dateWords(r.appointment_date)}</div>}
                      </td>
                      <td data-label="Time">
                        <TimeCell value={r.appointment_time} onChange={(v) => setCell(i, 'appointment_time', v)} readOnly={ro} />
                      </td>
                      <td data-label="Issues">
                        <div className="issues">
                          {(r.issues ?? []).map((iss, k) => (
                            <span key={k} className={`chip ${isBlocking(iss) ? 'danger' : 'warn'}`}>
                              {iss}
                            </span>
                          ))}
                        </div>
                      </td>
                      {imp.status === 'review' && (
                        <td className="right">
                          <button className="btn link small" onClick={() => dropRow(i)} title="Leave this row out">
                            Remove
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {imp.status === 'review' && rows.length === 0 && (
            <div className="center" style={{ padding: 24 }}>
              <button className="btn primary" onClick={addRow}>
                Add the first row
              </button>
            </div>
          )}
          {imp.status === 'review' && (
            <div className="tiny muted" style={{ padding: '8px 16px' }}>
              Red = will be skipped. Amber = check it. Click a cell to fix it; the phone number is checked again when you save. For the time you can put a single time
              (2:00 PM) or a time frame (2-4pm); the box always reads it back the way the customer will see it.
            </div>
          )}
        </div>
      )}

      {imp.status === 'review' && rows.length === 0 && !typed && (
        <div className="notice warn" style={{ marginTop: 12 }}>The reader found no rows. Try a clearer picture, add a note that says where the names and numbers are, or type the rows in above.</div>
      )}

      {imp.status === 'review' && !result && (
        <div className="card">
          <h2>Schedule the texts</h2>
          <label className="field">
            <span>Wording (template)</span>
            <select className="input" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
              <option value="">Choose a template</option>
              {orderedTemplates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} · {CATEGORY_LABEL[categoryOf(t)]} · {t.variants.length} {t.variants.length === 1 ? 'wording' : 'wordings'}
                </option>
              ))}
            </select>
            <div className="field-help">
              {'{{name}}'}, {'{{date}}'}, {'{{time}}'} and {'{{company}}'} are filled from each row. {'{{time}}'} says exactly what you put in the Time box, so "2-4pm" reads as
              "around 2-4pm". <Link to="/scheduled/templates">Manage templates</Link>
            </div>
          </label>
          {fewWordings && (
            <div className="notice warn" style={{ marginBottom: 12 }}>
              This template has fewer than 3 wordings. Sending the same text to a whole list looks automated to the carrier, so the app will not schedule it. Add wordings under Scheduled › Templates.
            </div>
          )}
          {template && template.variants[0] && <div className="preview-box small" style={{ marginBottom: 12 }}>{template.variants[0]}</div>}

          {activeLines.length > 1 && (
            <label className="field">
              <span>Send from</span>
              <select className="input" value={lineId} onChange={(e) => setLineId(e.target.value)}>
                {activeLines.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.label ? `${l.label} · ` : ''}
                    {formatPhone(l.phone_number)}
                  </option>
                ))}
              </select>
            </label>
          )}

          <div className="field-label">When</div>
          <label className="check">
            <input type="radio" checked={when === 'one_time'} onChange={() => setWhen('one_time')} />
            <span>
              <strong>One time for everyone</strong>
              {when === 'one_time' && (
                <div className="row" style={{ marginTop: 6 }}>
                  <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ width: 'auto' }} />
                  <input className="input" type="time" value={time} onChange={(e) => setTime(e.target.value)} style={{ width: 'auto' }} />
                  <span className="muted small">office time</span>
                </div>
              )}
              {when === 'one_time' && inPast && <div className="tiny danger-text">That time has already passed.</div>}
            </span>
          </label>
          <label className="check">
            <input type="radio" checked={when === 'per_row'} onChange={() => setWhen('per_row')} />
            <span>
              <strong>Each row's own appointment</strong> <span className="muted small">(rows without a date and time are skipped)</span>
              {when === 'per_row' && (
                <div className="row wrap" style={{ marginTop: 6 }}>
                  <select className="input" value={offset} onChange={(e) => setOffset(e.target.value as Offset)} style={{ width: 'auto' }}>
                    <option value="day_before_6pm">The day before at 6:00 PM</option>
                    <option value="two_hours_before">2 hours before</option>
                    <option value="custom">Hours before, my choice</option>
                  </select>
                  {offset === 'custom' && (
                    <span className="row">
                      <input className="input" type="number" min={0} max={168} step={0.5} value={customHours} onChange={(e) => setCustomHours(e.target.value)} style={{ width: 90 }} />
                      <span className="muted small">hours before</span>
                    </span>
                  )}
                </div>
              )}
            </span>
          </label>

          <div className="notice info" style={{ margin: '12px 0' }}>
            This is for your own customers (service visits, appointments), not marketing. Opted-out and bad numbers are skipped, duplicates are collapsed, texts wait for texting hours, and on a phone line at most{' '}
            {me.workspace.phone_daily_first_touch_cap ?? 50} first-time contacts go out per day; the rest roll to the next day at the same time.
          </div>

          <div className="row between wrap">
            <span className="muted small">
              {summary.ready} ready · {summary.skipped} will be skipped
            </span>
            <span className="row">
              <button className="btn" onClick={() => confirm('Cancel this list? Nothing has been scheduled from it.') && cancel.mutate()} disabled={cancel.isPending}>
                Cancel
              </button>
              <button className="btn primary" disabled={!canSchedule} onClick={() => schedule.mutate()}>
                {schedule.isPending ? 'Scheduling' : `Schedule ${summary.ready > 0 ? pluralize(summary.ready, 'text') : ''}`}
              </button>
            </span>
          </div>
          <InlineError error={schedule.error} />
        </div>
      )}
    </div>
  );
}

function Cell({
  value,
  onChange,
  readOnly,
  placeholder,
  type,
  display,
}: {
  value: string | null;
  onChange: (v: string) => void;
  readOnly: boolean;
  placeholder: string;
  type?: string;
  /** what a person reads when the row can no longer be edited ("Wed, Sep 9" instead of 2026-09-09) */
  display?: string;
}) {
  if (readOnly) return <span>{value ? display || value : <span className="faint">—</span>}</span>;
  return <input type={type ?? 'text'} value={value ?? ''} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />;
}

/**
 * The time box. It always reads the way a person says it ("1:30 PM"), never "13:30", so the office
 * sees exactly what the customer will get. A time frame ("2-4pm") stays word for word. You edit the
 * words; the app turns them back into a stored time when you save.
 */
function TimeCell({ value, onChange, readOnly }: { value: string | null; onChange: (v: string) => void; readOnly: boolean }) {
  const [draft, setDraft] = useState<string | null>(null);
  if (readOnly) return <span>{value ? clockWords(value) : <span className="faint">—</span>}</span>;
  return (
    <input
      type="text"
      value={draft ?? clockWords(value)}
      placeholder="2:00 PM or 2-4pm"
      onFocus={() => setDraft(clockWords(value))}
      onChange={(e) => {
        setDraft(e.target.value);
        onChange(e.target.value);
      }}
      onBlur={() => setDraft(null)}
    />
  );
}

function ResultPanel({ imp, result, tz }: { imp: ListImport; result: ImportScheduleResponse | null; tz: string }) {
  const count = result?.scheduled_count ?? imp.scheduled_count ?? 0;
  const skipped = result?.skipped ?? imp.skipped ?? [];
  const first = result?.first_send_at ?? imp.send_at ?? null;
  const last = result?.last_send_at ?? null;
  return (
    <div className="card" style={{ borderColor: '#bfe5cd', background: 'var(--ok-soft)' }}>
      <h2>{pluralize(count, 'text')} scheduled</h2>
      {first && (
        <div className="small">
          First goes out {fmtDateAtTime(first, tz)}
          {last && last !== first ? `, last ${fmtDateAtTime(last, tz)}` : ''}. Texts outside texting hours wait for the window.
        </div>
      )}
      {(result?.rolled_over ?? 0) > 0 && <div className="small">{pluralize(result!.rolled_over!, 'text')} rolled to later days because of the daily first-time cap on a phone line.</div>}
      {result?.note && <div className="small">{result.note}</div>}
      {skipped.length > 0 && (
        <details style={{ marginTop: 8 }}>
          <summary className="small">
            {pluralize(skipped.length, 'row')} skipped
          </summary>
          <ul className="small" style={{ margin: '6px 0 0 18px', padding: 0 }}>
            {skipped.map((s, i) => (
              <li key={i}>
                Row {typeof s.row === 'number' ? s.row + 1 : s.row}: {s.reason}
              </li>
            ))}
          </ul>
        </details>
      )}
      <div style={{ marginTop: 10 }}>
        <Link to="/scheduled" className="btn small">
          See them under Scheduled
        </Link>
      </div>
    </div>
  );
}
